/**
 * How a recording is coloured where the score has its player muted, or bowing at the bridge.
 *
 * VSCO-2 recorded neither way of playing, so each is heard as what it does to the sound the
 * recordings do have, a filter between the recording and the room:
 *
 * - **a string's mute** is a weight on the bridge, and takes down what the bridge passes of
 *   the upper partials: a shelf down over 1.5 kHz, where a violin's brightness is.
 * - **a brass mute** stops the bell and lets through what is over its own resonance, which
 *   Backus measured at about 1.8 kHz for a trumpet's straight mute: a high-pass there, and
 *   an octave lower for each octave the instrument is longer.
 * - **the bridge** is where a bow cannot set the whole string moving, so the fundamental is
 *   weak and the upper partials stand out: a high-pass an octave over the note, which
 *   follows it, and a shelf up over 3 kHz.
 *
 * A filter is not the recording that was never made. It is nearer than the same passage
 * played open, which is all there was, and it changes no note and no loudness asked for.
 */

/** What of a note says how it is coloured. */
export interface Manner {
  readonly program: number | null;
  readonly muted: boolean;
  readonly bridge: boolean;
}

/** Where a mute's high-pass turns, by the program of the brass it is in. */
const BRASS_MUTE: ReadonlyMap<number, number> = new Map([
  [56, 1800],
  [57, 900],
  [58, 450],
  [60, 900],
  [61, 900],
]);

const STRING_MUTE = { hz: 1500, gain: -12 };
const BRIDGE = { octaves: 2, hz: 3000, gain: 6 };

function filter(
  context: BaseAudioContext,
  type: BiquadFilterType,
  hz: number,
  gain = 0,
): BiquadFilterNode {
  const made = context.createBiquadFilter();
  made.type = type;
  made.frequency.value = Math.min(hz, context.sampleRate / 2);
  made.gain.value = gain;
  return made;
}

/**
 * The filters a note of `hz` is played through for its `manner`, in order; none for a note
 * played open and in the ordinary place, which is nearly every note.
 */
export function colouredBy(
  context: BaseAudioContext,
  { program, muted, bridge }: Manner,
  hz: number,
): readonly BiquadFilterNode[] {
  const through: BiquadFilterNode[] = [];
  if (muted) {
    const brass = program === null ? undefined : BRASS_MUTE.get(program);
    through.push(
      brass === undefined
        ? filter(context, "highshelf", STRING_MUTE.hz, STRING_MUTE.gain)
        : filter(context, "highpass", brass),
    );
  }
  if (bridge) {
    through.push(
      filter(context, "highpass", hz * BRIDGE.octaves),
      filter(context, "highshelf", BRIDGE.hz, BRIDGE.gain),
    );
  }
  return through;
}
